import { clearAuthSession, getAccessToken, getIdToken } from "./authSessionService";

export const BASE_URL = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";

export class ApiError extends Error {
  readonly status: number;

  constructor(message: string, status: number) {
    super(message);
    this.status = status;
  }
}

type ApiRequestOptions = {
  readonly requiresAuth?: boolean;
  readonly redirectOnUnauthorized?: boolean;
};

function toApiUrl(path: string): string {
  if (path.startsWith("http://") || path.startsWith("https://")) {
    return path;
  }
  let rel = path.startsWith("/") ? path : `/${path}`;
  const base = BASE_URL.replace(/\/$/, "");
  // If callers pass `/api/v1/...` while BASE_URL already ends with `/api/v1`, avoid `/api/v1/api/v1/...`
  if (rel.startsWith("/api/v1/") && (base === "/api/v1" || base.endsWith("/api/v1"))) {
    rel = rel.slice("/api/v1".length);
    if (!rel.startsWith("/")) {
      rel = `/${rel}`;
    }
  }
  return `${base}${rel}`;
}

async function parseApiError(response: Response): Promise<string> {
  const raw = await response.text();
  if (!raw) return `Request failed (${response.status})`;
  try {
    const parsed = JSON.parse(raw) as { detail?: string; message?: string };
    return parsed.detail ?? parsed.message ?? raw;
  } catch {
    return raw;
  }
}

function handleUnauthorizedRedirect(): void {
  clearAuthSession();
  if (window.location.pathname !== "/login") {
    window.location.replace("/login");
  }
}

export async function apiRequest(
  path: string,
  init?: RequestInit,
  options?: ApiRequestOptions
): Promise<Response> {
  const requiresAuth = options?.requiresAuth ?? true;
  const redirectOnUnauthorized = options?.redirectOnUnauthorized ?? true;
  const headers = new Headers(init?.headers ?? {});

  if (requiresAuth) {
    const token = getAccessToken() ?? getIdToken();
    if (token) {
      headers.set("Authorization", `Bearer ${token}`);
    }
  }

  const response = await fetch(toApiUrl(path), {
    ...init,
    headers,
  });

  if (response.status === 401 && redirectOnUnauthorized && requiresAuth) {
    handleUnauthorizedRedirect();
  }

  if (!response.ok) {
    const message = await parseApiError(response);
    throw new ApiError(message, response.status);
  }

  return response;
}

